import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { SlidersHorizontal, X, Map as MapIcon, List } from "lucide-react";
import SearchBar from "../components/SearchBar";
import VehicleCard from "../components/VehicleCard";
import VehicleMap from "../components/VehicleMap";
import { Slider } from "../components/ui/slider";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import api from "../lib/api";

const TYPES = ["compact", "sedan", "suv", "bakkie", "hatchback", "convertible", "luxury"];
const TRANS = ["automatic", "manual"];
const FUELS = ["petrol", "diesel", "hybrid", "electric"];

export default function VehicleSearch() {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [maxPrice, setMaxPrice] = useState(Number(params.get("max_price")) || 4000);
  const [transmission, setTransmission] = useState(params.get("transmission") || "");
  const [fuel, setFuel] = useState(params.get("fuel_type") || "");
  const [delivery, setDelivery] = useState(params.get("delivery") === "true");
  const [driver, setDriver] = useState(params.get("driver") === "true");
  const [sort, setSort] = useState(params.get("sort") || "recommended");
  const [showMap, setShowMap] = useState(params.get("view") === "map");
  const [hoverId, setHoverId] = useState(null);

  const city = params.get("city") || "";
  const vehicleType = params.get("vehicle_type") || "";

  useEffect(() => {
    setLoading(true);
    const p = {};
    if (city) p.city = city;
    if (vehicleType) p.vehicle_type = vehicleType;
    if (transmission) p.transmission = transmission;
    if (fuel) p.fuel_type = fuel;
    if (delivery) p.delivery = true;
    if (driver) p.driver = true;
    if (maxPrice && maxPrice < 4000) p.max_price = maxPrice;
    if (sort) p.sort = sort;
    api.get("/vehicles", { params: p })
      .then((r) => setVehicles(r.data || []))
      .finally(() => setLoading(false));
  }, [city, vehicleType, transmission, fuel, delivery, driver, maxPrice, sort]);

  const applyType = (t) => {
    const next = new URLSearchParams(params);
    if (t) next.set("vehicle_type", t); else next.delete("vehicle_type");
    setParams(next);
  };

  return (
    <div className="relative">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 pt-8">
        <SearchBar initial={{ city, vehicle_type: vehicleType }} />

        {/* type chips */}
        <div className="mt-6 flex items-center gap-2 overflow-x-auto no-scrollbar pb-1">
          <button
            onClick={() => applyType("")}
            className={`px-4 py-2 rounded-full text-sm font-semibold border whitespace-nowrap transition ${
              !vehicleType ? "bg-[#08153a] text-white border-[#08153a]" : "bg-white text-[#08153a] border-[#E2E8F0] hover:border-[#08153a]"
            }`}
            data-testid="filter-type-all"
          >
            All cars
          </button>
          {TYPES.map((t) => (
            <button
              key={t}
              onClick={() => applyType(t)}
              className={`px-4 py-2 rounded-full text-sm font-semibold border whitespace-nowrap transition capitalize ${
                vehicleType === t ? "bg-[#08153a] text-white border-[#08153a]" : "bg-white text-[#08153a] border-[#E2E8F0] hover:border-[#08153a]"
              }`}
              data-testid={`filter-type-${t}`}
            >
              {t}
            </button>
          ))}
          <button
            onClick={() => setShowFilters((s) => !s)}
            className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border border-[#E2E8F0] text-[#08153a] hover:border-[#08153a]"
            data-testid="filter-toggle"
          >
            <SlidersHorizontal size={14} /> Filters
          </button>
          <button
            onClick={() => setShowMap((s) => !s)}
            className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-semibold border transition ${showMap ? "bg-[#08153a] text-white border-[#08153a]" : "border-[#E2E8F0] text-[#08153a] hover:border-[#08153a]"}`}
            data-testid="map-toggle"
          >
            {showMap ? <List size={14} /> : <MapIcon size={14} />} {showMap ? "List" : "Map"}
          </button>
          <Select value={sort} onValueChange={setSort}>
            <SelectTrigger className="w-[170px] rounded-full border-[#E2E8F0] font-semibold" data-testid="sort-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="recommended">Recommended</SelectItem>
              <SelectItem value="price_asc">Price: low to high</SelectItem>
              <SelectItem value="price_desc">Price: high to low</SelectItem>
              <SelectItem value="rating">Top rated</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12 mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8">
        {showFilters && (
          <aside className="lg:col-span-3 bg-white border border-[#E2E8F0] rounded-2xl p-6 h-fit lg:sticky lg:top-24">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-display font-bold text-[#08153a]">Filters</h3>
              <button onClick={() => setShowFilters(false)} className="text-[#64748B] hover:text-[#08153a]" aria-label="Close filters">
                <X size={16} />
              </button>
            </div>

            <div>
              <p className="text-xs tracking-[0.16em] uppercase font-bold text-[#64748B] mb-3">Max price / day</p>
              <Slider value={[maxPrice]} min={400} max={4000} step={100} onValueChange={(v) => setMaxPrice(v[0])} data-testid="filter-price-slider" />
              <p className="text-sm text-[#08153a] font-semibold mt-3">Up to R{maxPrice}</p>
            </div>

            <div className="mt-6">
              <p className="text-xs tracking-[0.16em] uppercase font-bold text-[#64748B] mb-3">Transmission</p>
              <div className="flex gap-2">
                {TRANS.map((t) => (
                  <button
                    key={t}
                    onClick={() => setTransmission(transmission === t ? "" : t)}
                    className={`flex-1 px-3 py-2 text-sm rounded-full border capitalize ${
                      transmission === t ? "bg-[#08153a] text-white border-[#08153a]" : "bg-white text-[#08153a] border-[#E2E8F0]"
                    }`}
                    data-testid={`filter-trans-${t}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6">
              <p className="text-xs tracking-[0.16em] uppercase font-bold text-[#64748B] mb-3">Fuel</p>
              <div className="grid grid-cols-2 gap-2">
                {FUELS.map((t) => (
                  <button
                    key={t}
                    onClick={() => setFuel(fuel === t ? "" : t)}
                    className={`px-3 py-2 text-sm rounded-full border capitalize ${
                      fuel === t ? "bg-[#08153a] text-white border-[#08153a]" : "bg-white text-[#08153a] border-[#E2E8F0]"
                    }`}
                    data-testid={`filter-fuel-${t}`}
                  >
                    {t}
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-6 space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox checked={delivery} onCheckedChange={(v) => setDelivery(!!v)} data-testid="filter-delivery" />
                <span className="text-sm text-[#08153a] font-medium">Delivery available</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <Checkbox checked={driver} onCheckedChange={(v) => setDriver(!!v)} data-testid="filter-driver" />
                <span className="text-sm text-[#08153a] font-medium">With chauffeur option</span>
              </label>
            </div>

            <button
              onClick={() => { setMaxPrice(4000); setTransmission(""); setFuel(""); setDelivery(false); setDriver(false); }}
              className="mt-7 w-full btn-outline !py-2.5 text-sm"
              data-testid="filter-reset"
            >
              Reset filters
            </button>
          </aside>
        )}

        <div className={showFilters ? "lg:col-span-9" : "lg:col-span-12"}>
          <div className="flex items-baseline justify-between mb-5">
            <h1 className="font-display text-2xl sm:text-3xl font-bold text-[#08153a]">
              {loading ? "Searching cars…" : `${vehicles.length} cars${city ? ` in ${city}` : " across SA"}`}
            </h1>
          </div>

          {!loading && vehicles.length === 0 && (
            <div className="border border-[#E2E8F0] rounded-2xl p-10 text-center bg-[#F7F8FA]">
              <p className="text-[#08153a] font-display font-bold text-lg">No cars match your filters.</p>
              <p className="text-sm text-[#64748B] mt-1">Try clearing filters or browsing a different city.</p>
            </div>
          )}

          {showMap ? (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 order-2 lg:order-1" data-testid="search-results-grid">
                {vehicles.map((v) => (
                  <div key={v.id} onMouseEnter={() => setHoverId(v.id)} onMouseLeave={() => setHoverId(null)} className={hoverId === v.id ? "ring-2 ring-[#1d7ffd] rounded-[1.25rem]" : ""}>
                    <VehicleCard v={v} />
                  </div>
                ))}
              </div>
              <VehicleMap vehicles={vehicles} activeId={hoverId} onHover={setHoverId} className="order-1 lg:order-2 h-[420px] lg:h-[calc(100vh-9rem)] lg:sticky lg:top-24" />
            </div>
          ) : (
            <div className={`grid grid-cols-1 sm:grid-cols-2 ${showFilters ? "lg:grid-cols-2 xl:grid-cols-3" : "lg:grid-cols-3 xl:grid-cols-4"} gap-6`} data-testid="search-results-grid">
              {vehicles.map((v) => <VehicleCard key={v.id} v={v} />)}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
